/**
 * Settings > My alerts (tracker item 62): which kinds of staff alerts the
 * signed-in user gets in the bell. Each switch saves at once; a muted kind
 * creates no new alert for this user (alerts already in the list stay until
 * read). Personal: it never changes what anybody else gets. ADMIN /
 * OFFICE_MANAGER only.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IStaffNotificationsApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canViewStaffNotifications } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { STAFF_NOTIFICATIONS_PATH } from '../../utils/staffNotifications';
import { Toast } from '../../components/Toast';

export function StaffAlertPreferencesPage() {
  const { user } = useAuth();
  const api = useMemo<IStaffNotificationsApi>(() => createAPIClient(), []);
  const allowed = canViewStaffNotifications(user);
  const [prefs, setPrefs] = useState<types.StaffNotificationPreference[] | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        setPrefs((await api.getStaffNotificationPreferences()).types);
      } catch (err) {
        setError(getErrorMessage(err, 'Could not load your alert settings'));
      }
    })();
  }, [api, allowed]);

  if (!allowed) {
    return <p className="p-4 text-sm text-gray-600">Alert settings are available to admins and office managers.</p>;
  }

  const toggle = async (pref: types.StaffNotificationPreference) => {
    setSaving(pref.type);
    setError(null);
    try {
      const saved = await api.setStaffNotificationPreferences({ [pref.type]: !pref.muted } as Partial<Record<types.StaffNotificationType, boolean>>);
      setPrefs(saved.types);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save your alert settings'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <main className="p-4 sm:p-6 max-w-2xl space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">My alerts</h1>
        <p className="mt-1 text-sm text-gray-600">
          Choose which alerts appear in your <Link to={STAFF_NOTIFICATIONS_PATH} className="text-blue-600 hover:underline">notifications</Link> (the bell). This only changes what you see; other people keep their own settings. Alerts already in your list stay until you read them.
        </p>
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {!prefs && !error && <p className="text-sm text-gray-500">Loading...</p>}
      {prefs && (
        <fieldset className="space-y-2">
          <legend className="sr-only">Alert types</legend>
          {prefs.map((pref) => (
            <label key={pref.type} className="flex items-start gap-3 rounded-md border border-gray-200 bg-white p-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={!pref.muted}
                disabled={saving !== null}
                onChange={() => void toggle(pref)}
                aria-label={pref.label}
              />
              <span>
                <span className="block text-sm font-semibold text-gray-900">{pref.label}</span>
                <span className="block text-xs text-gray-600">{pref.description}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </main>
  );
}
